import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../Context/AuthContext";
import { Authorization } from "../Context/Authorization";

const JfmcTypePopup = () => {
  const { auth, selectedJfmcType, role, setSelectedJfmcType, jfmcTypeReady, userId } = useContext(AuthContext);
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const Api = process.env.REACT_APP_API;

  /* Only show when user is logged in and no type is selected */
  const normalizedRole = String(role || "")
    .trim()
    .toLowerCase();
  const isAdmin = [
    "admin",
    "super admin",
  ].includes(normalizedRole);
  const visible =
    Boolean(auth) &&
    Boolean(userId) &&
    jfmcTypeReady &&
    !selectedJfmcType &&
    !isAdmin;
  useEffect(() => {
    if (!auth || !userId || !jfmcTypeReady) return;

    let cancelled = false;

    const fetchTypes = async () => {
      setLoading(true);

      try {
        const config = Authorization();

        const res = await fetch(`${Api}/api/jfmc-Types`, {
          headers: config.headers,
        });

        if (!res.ok) {
          throw new Error("Failed to fetch JFMC types");
        }

        const data = await res.json();
        if (cancelled) return;

        const list = Array.isArray(data) ? data : [];
        setTypes(list);

        const defaultVfcType = list.find(
          (type) =>
            String(type.jfmcTypeName || "").trim().toUpperCase() ===
            "VFC"
        );

        // Admin users automatically use VFC, if available.
        if (isAdmin) {
          if (defaultVfcType) {
            setSelectedJfmcType(defaultVfcType);
          }
          return;
        }

        // A single available type can be selected automatically.
        if (list.length === 1) {
          setSelected(list[0]);
          setSelectedJfmcType(list[0]);
          return;
        }

        setSelected(null);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch JFMC types:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTypes();

    return () => {
      cancelled = true;
    };
  }, [auth, userId, jfmcTypeReady, isAdmin, Api, setSelectedJfmcType]);

  const handleConfirm = () => {
    if (!selected) return;
    setSubmitting(true);
    setSelectedJfmcType(selected);
    setSubmitting(false);
  };

  if (!visible) return null;

  return (
    /* Backdrop */
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(10, 26, 10, 0.75)",
      backdropFilter: "blur(6px)",
      zIndex: 2000,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      {/* Card */}
      <div style={{
        background: "#fff",
        borderRadius: 16,
        width: "100%",
        maxWidth: 480,
        maxHeight: "90vh", // important
        boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
        overflow: "hidden",
        animation: "popupIn 0.3s ease",
        display: "flex",
        flexDirection: "column",
      }}>

        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #1B5E20, #2E7D32)",
          padding: "22px 28px",
          display: "flex", alignItems: "center", gap: 14,
        }}>
          <div style={{
            width: 44, height: 44,
            background: "rgba(255,255,255,0.15)",
            borderRadius: 12,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <i className="mdi mdi-tag-multiple" style={{ color: "#fff", fontSize: 22 }} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
              Select JFMC Type
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", marginTop: 3 }}>
              Choose your working JFMC category to continue
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            padding: "28px 28px 24px",
            overflowY: "auto",
            flex: 1,
          }}
        >

          {loading ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{
                width: 40, height: 40, border: "3px solid #E8F5E9",
                borderTopColor: "#2E7D32", borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto 14px",
              }} />
              <div style={{ fontSize: 13, color: "#718096" }}>Loading JFMC types…</div>
            </div>
          ) : types.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#A0AEC0", fontSize: 13 }}>
              No JFMC types found. Please contact the administrator.
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, color: "#4A5568", marginBottom: 18, lineHeight: 1.6 }}>
                Please select the JFMC type you are managing. This will filter all data and reports across the platform.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  maxHeight: "45vh",
                  overflowY: "auto",
                  paddingRight: 4,
                }}
              >
                {types.map((type) => {
                  const isActive = selected?._id === type._id;
                  return (
                    <button
                      key={type._id}
                      onClick={() => setSelected(type)}
                      style={{
                        display: "flex", alignItems: "center", gap: 14,
                        padding: "14px 18px",
                        border: `2px solid ${isActive ? "#2E7D32" : "#E2E8F0"}`,
                        borderRadius: 12,
                        background: isActive ? "#E8F5E9" : "#fff",
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "all 0.15s",
                        width: "100%",
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.borderColor = "#A5D6A7"; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.borderColor = "#E2E8F0"; }}
                    >
                      {/* Radio indicator */}
                      <div style={{
                        width: 20, height: 20, borderRadius: "50%",
                        border: `2px solid ${isActive ? "#2E7D32" : "#CBD5E0"}`,
                        background: isActive ? "#2E7D32" : "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, transition: "all 0.15s",
                      }}>
                        {isActive && (
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />
                        )}
                      </div>

                      {/* Icon */}
                      <div style={{
                        width: 36, height: 36, borderRadius: 9,
                        background: isActive ? "#2E7D32" : "#F7FAFC",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, transition: "all 0.15s",
                      }}>
                        <i className="mdi mdi-leaf" style={{ color: isActive ? "#fff" : "#718096", fontSize: 17 }} />
                      </div>

                      {/* Label */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: isActive ? "#1B5E20" : "#1A202C" }}>
                          {type.jfmcTypeName}
                        </div>
                        {type.description && (
                          <div style={{ fontSize: 12, color: "#718096", marginTop: 2 }}>{type.description}</div>
                        )}
                      </div>

                      {isActive && (
                        <i className="mdi mdi-check-circle" style={{ color: "#2E7D32", fontSize: 20, flexShrink: 0 }} />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Footer buttons */}
          {!loading && types.length > 0 && (
            <div
              style={{
                marginTop: 24,
                display: "flex",
                gap: 10,
                position: "sticky",
                bottom: 0,
                background: "#fff",
                paddingTop: 12,
              }}
            >
              <button
                onClick={handleConfirm}
                disabled={!selected || submitting}
                style={{
                  flex: 1,
                  padding: "12px 20px",
                  background: selected ? "linear-gradient(135deg, #1B5E20, #2E7D32)" : "#E2E8F0",
                  color: selected ? "#fff" : "#A0AEC0",
                  border: "none",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: selected ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  boxShadow: selected ? "0 4px 14px rgba(46,125,50,0.4)" : "none",
                  transition: "all 0.2s",
                }}
              >
                <i className="mdi mdi-check" />
                {submitting ? "Setting up…" : "Proceed"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Keyframe styles injected inline */}
      <style>{`
        @keyframes popupIn {
          from { opacity: 0; transform: scale(0.92) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default JfmcTypePopup;
